import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import {
    AspectRatio,
    type CardKindViewType,
    type CardSettingsFormType,
    CardViewType,
    type InfoSettingsFormType,
    type PreviewModalSettingsFormType,
    type ProductButtonFormType,
    type ProductCardDataFormType,
    ViewModalType,
} from '@web/widgets/product-card/type';
import {
    cardSettingsStyles,
    cardTypeOptions,
    productViewCardIcons,
} from '@web/widgets/product-card/constants/defaults-product-card.conts';

import type { UntypedFormControl } from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CurrentSettings } from '@web/widgets/product-card/components/product-card-control/product-card-control.component';
import { EGalleryTypes, Widgets, type EHorizontalPosition } from '@common/enums';
import { ERadioGroupType } from '@ui/sp-radio-group';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { EControlHandleEvents } from '@ui/sp-control-handle/src/sp-control-handle.enum';

import { SpRadioGroupComponent } from '@ui/sp-radio-group';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { SpControlHandleComponent } from '@ui/sp-control-handle';
import { CommonModule } from '@angular/common';
import { environment } from '@web-env/environment';
import type { SelectedFileExtended } from '@common/types/file.type';
import type { Observable } from 'rxjs';
import { ProductRestrictionFacade } from '@web/core/services/product-restriction.facade';
import { LetDirective } from '@ngrx/component';
import { WebCustomIdComponent } from '@web/shared/components/web-custom-id/web-custom-id.component';
import { buildFilePreviewUrls } from '@components/sp-file-manager/src/helpers';

@Component({
    selector: 'web-card-settings',
    templateUrl: './card-settings.component.html',
    styleUrls: ['./card-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        CommonModule,
        SpControlHandleComponent,
        FormsModule,
        ReactiveFormsModule,
        TooltipModule,
        SpRadioGroupComponent,
        TranslateModule,
        LetDirective,
        WebCustomIdComponent,
    ],
})
export class CardSettingsComponent {
    @Input() data: UntypedFormControl;
    @Input() widgetId: string;
    @Input() displayId: string;
    @Output() setCurrentSettingsType: EventEmitter<CurrentSettings> = new EventEmitter<CurrentSettings>();
    @Output() editProductCard: EventEmitter<void> = new EventEmitter<void>();
    @Output() updateWidgetDisplayId: EventEmitter<string> = new EventEmitter<string>();
    @Output() openCatalogEvent: EventEmitter<void> = new EventEmitter<void>();

    protected readonly cardTypeOptions = cardTypeOptions;
    protected readonly productViewCardIcons = productViewCardIcons;
    protected readonly settingEnum = CurrentSettings;
    protected readonly viewModalType = ViewModalType;

    private readonly productRestrictionFacade: ProductRestrictionFacade = inject(ProductRestrictionFacade);
    private readonly translateService: TranslateService = inject(TranslateService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);

    public removeProduct() {
        this.element.reset();
        this.element.controls.sections.clear();
        this.element.controls.images.clear();
        this.data.patchValue({ isShowCardSettings: false });
    }

    public setCardKindViewType(value: CardKindViewType) {
        this.cardKindViewType.patchValue(value);
        this.setPresetStyles(value);

        const swiperWrapper = document.querySelector(`[id='${this.widgetId}'] .swiper-wrapper`) as HTMLDivElement;

        if (swiperWrapper) {
            if (this.galleryCardSettings.value.aspectRatio !== AspectRatio.default) {
                swiperWrapper.style.removeProperty('height');
            } else {
                setTimeout(() => {
                    swiperWrapper.style.height = 'auto';
                }, 100);
            }
        }
    }

    public setPresetStyles(value: CardKindViewType) {
        const presetStyles = cardSettingsStyles[value];

        this.generalCardSettings.patchValue(presetStyles.general);
        this.infoCardSettings.patchValue(presetStyles.info);
        this.galleryCardSettings.patchValue(presetStyles.gallery);
    }

    public setModalView(value: ViewModalType) {
        if (value === ViewModalType.vertical) {
            this.setPaddingTop(30);
            this.setPaddingLeft(0);
        }

        if (value === ViewModalType.horizontal) {
            this.setPaddingLeft(50);
            this.setPaddingTop(0);
        }
        this.cardModalViewType.patchValue(value);
    }

    public setIsShowButton(value: boolean) {
        this.isShowButton.patchValue(value);
    }

    public setPreviewHorizontalPosition(val: string): void {
        this.previewHorizontalPosition.patchValue(val as EHorizontalPosition);
    }

    public setPaddingTop(value: number): void {
        this.paddingTop.setValue(value);
    }

    public setPaddingLeft(value: number): void {
        this.paddingLeft.setValue(value);
    }

    public changeViewType(): void {
        this.layoutFacade.setIsHideColumnControls(this.isOpenCardView);
    }

    public get isOpenCardView(): boolean {
        return this.cardViewType.value === CardViewType.open;
    }

    public get previewModalSettings(): ProductCardDataFormType['previewModalSettings'] {
        return this.data.get('previewModalSettings') as ProductCardDataFormType['previewModalSettings'];
    }

    public get cardSettings(): ProductCardDataFormType['cardSettings'] {
        return this.data.get('cardSettings') as ProductCardDataFormType['cardSettings'];
    }

    public get generalCardSettings(): CardSettingsFormType['general'] {
        return this.cardSettings.get('general') as CardSettingsFormType['general'];
    }

    public get galleryCardSettings(): CardSettingsFormType['gallery'] {
        return this.cardSettings.get('gallery') as CardSettingsFormType['gallery'];
    }

    public get infoCardSettings(): CardSettingsFormType['info'] {
        return this.cardSettings.get('info') as CardSettingsFormType['info'];
    }

    public get infoSettingsFormGroup(): PreviewModalSettingsFormType['info'] {
        return this.previewModalSettings.get('info') as PreviewModalSettingsFormType['info'];
    }

    public get paddingTop(): InfoSettingsFormType['paddingTop'] {
        return this.infoSettingsFormGroup.get('paddingTop') as InfoSettingsFormType['paddingTop'];
    }

    public get paddingLeft(): InfoSettingsFormType['paddingLeft'] {
        return this.infoSettingsFormGroup.get('paddingLeft') as InfoSettingsFormType['paddingLeft'];
    }

    public get isModalCardView(): boolean {
        return this.cardViewType.value === CardViewType.modal;
    }

    public get isVerticalPreview(): boolean {
        return this.cardModalViewType.value === ViewModalType.vertical;
    }

    public get element(): ProductCardDataFormType['element'] {
        return this.data.get('element') as ProductCardDataFormType['element'];
    }

    public get subText(): string {
        return this.element.value.vendorCode
            ? `${this.translateService.instant('widget_product_vendor_code')}: ${this.element.value.vendorCode}`
            : '';
    }

    public get cardViewType(): ProductCardDataFormType['cardViewType'] {
        return this.data.get('cardViewType') as ProductCardDataFormType['cardViewType'];
    }

    public get cardKindViewType(): ProductCardDataFormType['cardKindViewType'] {
        return this.data.get('cardKindViewType') as ProductCardDataFormType['cardKindViewType'];
    }

    public get cardModalViewType(): ProductCardDataFormType['cardModalViewType'] {
        return this.data.get('cardModalViewType') as ProductCardDataFormType['cardModalViewType'];
    }

    public get basketButton(): ProductCardDataFormType['basketButton'] {
        return this.data.get('basketButton') as ProductCardDataFormType['basketButton'];
    }

    public get showMoreButton(): ProductCardDataFormType['showMoreButton'] {
        return this.data.get('showMoreButton') as ProductCardDataFormType['showMoreButton'];
    }

    public get buttonLabel(): ProductButtonFormType['label'] {
        return this.basketButton.get('label') as ProductButtonFormType['label'];
    }

    public get isShowButton(): ProductButtonFormType['isShowButton'] {
        return this.showMoreButton.get('isShowButton') as ProductButtonFormType['isShowButton'];
    }

    public get previewImageUrls(): string[] {
        if (!this.element?.value?.images?.length) {
            return [];
        }

        return buildFilePreviewUrls(this.element?.value?.images?.at(0), environment.fileManagerCdnUrl);
    }

    public get previewHorizontalPosition(): ProductCardDataFormType['previewHorizontalPosition'] {
        return this.data.get('previewHorizontalPosition') as ProductCardDataFormType['previewHorizontalPosition'];
    }

    public get createCardOption(): ProductCardDataFormType['createCardOption'] {
        return this.data.get('createCardOption') as ProductCardDataFormType['createCardOption'];
    }

    public get selectedFromCatalog(): ProductCardDataFormType['selectedFromCatalog'] {
        return this.data.get('selectedFromCatalog') as ProductCardDataFormType['selectedFromCatalog'];
    }

    public get isCatalogOption(): boolean {
        return this.selectedFromCatalog.value;
    }

    public get productWidgetRestricted$(): Observable<boolean> {
        return this.productRestrictionFacade.widgetRestricted$({
            name: Widgets.PRODUCT_CARD,
            displayId: this.displayId,
        } as any);
    }

    protected readonly EGalleryTypes = EGalleryTypes;
    protected readonly ERadioGroupType = ERadioGroupType;
    protected readonly EControlHandleEvents = EControlHandleEvents;
}
